import { useCallback, useEffect, useRef } from "react";

import { Editor } from "@tiptap/react";

import { useEditorRefStore } from "@demo/store/editor-ref";
import { Role } from "@demo/types/base";
import type { DocumentInputContentItem } from "@demo/types/documentContent";
import { isDocumentContentDirty } from "@demo/utils/editor";

export interface UseEditorDirtyStateProps {
  editor: Editor | null;
  editorRole: Role;
  savedDocumentContent: DocumentInputContentItem[];
  isLoadingDocument?: string | null;
}

export interface UseEditorDirtyStateReturn {
  isDirty: boolean;
  markClean: () => void;
  hasUnsavedChanges: () => boolean;
}

/**
 * Custom hook for managing editor dirty state.
 *
 * Features:
 * - Tracks whether content has changed from saved state
 * - Provides methods to mark content as clean
 * - Integrates with editor ref store for global state
 * - Uses DocumentContent comparison for accurate media handling
 * - Compares current TipTap JSON against saved API DocumentContent
 *
 * The dirty flag is written to the editor-ref store
 * (`isUserEditorContentDirty` / `isSystemEditorContentDirty`) so any surface —
 * e.g. a playground sync-status indicator — can read it reactively.
 *
 * @param props Configuration for dirty state tracking
 * @returns Dirty state and control methods
 */
export function useEditorDirtyState({
  editor,
  editorRole,
  savedDocumentContent,
  isLoadingDocument,
}: UseEditorDirtyStateProps): UseEditorDirtyStateReturn {
  const setEditorDirtyState = useEditorRefStore((state) => state.setEditorDirtyState);
  const isDirty = useEditorRefStore((state) =>
    editorRole === Role.USER ? state.isUserEditorContentDirty : state.isSystemEditorContentDirty,
  );

  // Use a ref to always have access to the latest savedDocumentContent
  const savedDocumentContentRef = useRef(savedDocumentContent);
  savedDocumentContentRef.current = savedDocumentContent;

  // Check if editor has unsaved changes
  const hasUnsavedChanges = useCallback((): boolean => {
    if (!editor || editor.isDestroyed) return false;

    const currentContent = editor.getJSON();
    // Use the ref to get the latest saved document content
    return isDocumentContentDirty(currentContent, savedDocumentContentRef.current, editorRole);
  }, [editor, editorRole]);

  // Mark content as clean (saved)
  const markClean = useCallback(() => {
    if (!editor || editor.isDestroyed) return;

    setEditorDirtyState(editorRole, false);
  }, [editor, editorRole, setEditorDirtyState]);

  // Check dirty state when content changes
  useEffect(() => {
    if (!editor) return;

    const checkDirtyState = () => {
      const currentIsDirty = hasUnsavedChanges();
      setEditorDirtyState(editorRole, currentIsDirty);
    };

    // Check on every transaction
    const handleUpdate = () => {
      checkDirtyState();
    };

    editor.on("update", handleUpdate);

    // Initial check
    checkDirtyState();

    return () => {
      editor.off("update", handleUpdate);
    };
  }, [editor, editorRole, hasUnsavedChanges, setEditorDirtyState]);

  // Update dirty state when saved document content changes
  useEffect(() => {
    // Skip dirty check while document is loading
    if (isLoadingDocument) {
      return;
    }

    if (editor && !editor.isDestroyed) {
      const currentContent = editor.getJSON();
      const newIsDirty = isDocumentContentDirty(currentContent, savedDocumentContent, editorRole);
      setEditorDirtyState(editorRole, newIsDirty);
    }
  }, [savedDocumentContent, editor, editorRole, setEditorDirtyState, isLoadingDocument]);

  return {
    isDirty,
    markClean,
    hasUnsavedChanges,
  };
}
